import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { deleteObject, getBytes, getDownloadURL, getMetadata, listAll, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../backend/firebaseConfig";
import { invalidateLogoCache } from "../../context/Adviser/Utils/FetchLogo";
import { normaliseStatementEmails } from "../Adviser/Tabs/Rates/statementRecipients";

const API_URL = (process.env.REACT_APP_SNOWFLAKE_API_URL || "http://localhost:10001").replace(/\/$/, "");
const GROUPS_PATH = ["Authorized Members", "Groups", "Data"];
const ADVISERS_PATH = ["Authorized Members", "Advisers", "Data"];

export const getGroupDetails = async (userEmail = "") => {
  const token = await auth.currentUser?.getIdToken(true);
  if (!token) throw new Error("Please sign in again.");
  const response = await fetch(`${API_URL}/api/group-details`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-User-Email": userEmail || "",
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || "Unable to load group details.");
  return body;
};

const toIdList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(","))
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);

const isTimestampField = (key = "") =>
  /(^|_)(date|timestamp|updatedat|createdat|syncedat|at)$/i.test(key) ||
  /(date|timestamp|updatedAt|createdAt|syncedAt)$/i.test(key);

const toISODateString = (value) => {
  if (!value) return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === "function") return value.toDate().toISOString();
  if (
    typeof value === "object" &&
    Number.isFinite(value.seconds) &&
    Number.isFinite(value.nanoseconds)
  ) {
    return new Date(
      value.seconds * 1000 + Math.round(value.nanoseconds / 1000000),
    ).toISOString();
  }

  const text = String(value);
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
};

const normaliseFirestoreValue = (value, key = "") => {
  if (value === undefined) return null;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value.map((item) => normaliseFirestoreValue(item, key));
  }
  if (value instanceof Date || typeof value?.toDate === "function") {
    return toISODateString(value);
  }
  if (typeof value === "object") {
    if (
      Number.isFinite(value.seconds) &&
      Number.isFinite(value.nanoseconds)
    ) {
      return toISODateString(value);
    }

    return Object.fromEntries(
      Object.entries(value).map(([childKey, childValue]) => [
        childKey,
        normaliseFirestoreValue(childValue, childKey),
      ]),
    );
  }

  return isTimestampField(key) ? toISODateString(value) : value;
};

const extractBandDetails = (group = {}) =>
  Object.fromEntries(
    Object.entries(group)
      .filter(([key]) => /band/i.test(key))
      .map(([key, value]) => [key, normaliseFirestoreValue(value, key)]),
  );

export const getGroupDetailsRef = (ioGroupId) =>
  doc(db, ...GROUPS_PATH, String(ioGroupId || "").trim());

export const getSavedGroupDetails = async (ioGroupId) => {
  const id = String(ioGroupId || "").trim();
  if (!id) return null;

  const snapshot = await getDoc(getGroupDetailsRef(id));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};

export const getSavedGroupRecords = async () => {
  const snapshot = await getDocs(collection(db, ...GROUPS_PATH));
  return snapshot.docs.map((groupDocument) => ({
    id: groupDocument.id,
    ...groupDocument.data(),
  }));
};

export const getGroupStatementRecipientCredentials = async (
  adviserLinks = [],
) => Promise.all(
  adviserLinks.map(async (adviserLink) => {
    const adviserId = String(adviserLink?.adviserId || "").trim();
    const ratesSnapshot = adviserId
      ? await getDoc(
          doc(
            db,
            ...ADVISERS_PATH,
            adviserId,
            "Rates",
            "Data",
          ),
        )
      : null;
    const rates = ratesSnapshot?.exists() ? ratesSnapshot.data() : {};
    return {
      email: adviserLink?.adviserEmail || "",
      adviser: adviserLink?.adviserName || adviserLink?.adviserEmail || "",
      dob: adviserLink?.dob || null,
      statementPassword: String(rates.statementPassword || "")
        .replace(/\s+/g, ""),
    };
  }),
);

export const resolveSavedGroupLogoUrl = async (group = {}) => {
  if (group.logoUrl) return group.logoUrl;
  const groupName = String(group.groupName || group.group?.GroupName || "").trim();
  const candidates = [
    group.logoStoragePath,
    groupName ? `Logos/${groupName}` : "",
    ...["png", "jpg", "jpeg", "webp", "PNG", "JPG", "JPEG"]
      .map((extension) => groupName ? `Logos/${groupName}.${extension}` : ""),
  ].filter(Boolean);

  for (const path of candidates) {
    try {
      return await getDownloadURL(ref(storage, path));
    } catch {
      // Continue through legacy group-logo storage paths.
    }
  }
  return "";
};

export const loadGroupAdviserLinks = async (ioGroupId) => {
  const id = String(ioGroupId || "").trim();
  if (!id) return [];

  const adviserSnapshot = await getDocs(collection(db, ...ADVISERS_PATH));
  const links = await Promise.all(
    adviserSnapshot.docs.map(async (adviserDocument) => {
      const adviser = adviserDocument.data() || {};
      const [ioSnapshot, personalSnapshot] = await Promise.all([
        getDoc(
          doc(
            db,
            ...ADVISERS_PATH,
            adviserDocument.id,
            "IO Details",
            "Data",
          ),
        ),
        getDoc(
          doc(
            db,
            ...ADVISERS_PATH,
            adviserDocument.id,
            "Personal Details",
            "Data",
          ),
        ),
      ]);
      const ioDetails = ioSnapshot.exists() ? ioSnapshot.data() : {};
      const personal = personalSnapshot.exists() ? personalSnapshot.data() : {};
      const groupIds = toIdList(ioDetails.groupId);

      if (!groupIds.includes(id)) return null;

      const adviserName = [
        personal.preferredName ||
          personal.forename ||
          personal.firstName ||
          adviser.preferredName ||
          adviser.firstName ||
          adviser.forename,
        personal.surname ||
          personal.lastName ||
          adviser.surname ||
          adviser.lastName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();

      return {
        adviserId: adviserDocument.id,
        adviserEmail: adviser.email || adviserDocument.id,
        adviserName:
          adviserName ||
          adviser.name ||
          adviser.displayName ||
          adviser.email ||
          adviserDocument.id,
        primaryColor: adviser.PrimaryColor || adviser.primaryColor || "",
        secondaryColor: adviser.SecondaryColor || adviser.secondaryColor || "",
        tertiaryColor: adviser.TertiaryColor || adviser.tertiaryColor || "",
        dob:
          personal.dob ||
          personal.dateOfBirth ||
          adviser.dob ||
          adviser.dateOfBirth ||
          null,
        ioGroupIds: groupIds,
      };
    }),
  );

  return links.filter(Boolean);
};

export const buildGroupRecord = (group = {}, adviserLinks = [], existing = {}) => {
  const ioGroupId = String(group.GroupId || group.groupId || "").trim();
  const syncedAt = new Date().toISOString();
  const brandingAdviser = adviserLinks.find((link) =>
    String(link.primaryColor || "").trim(),
  ) || adviserLinks[0] || {};
  const primaryColor =
    String(existing.primaryColor || "").trim() ||
    adviserLinks
      .map((link) => String(link.primaryColor || "").trim())
      .find(Boolean) || "";
  const normalisedGroup = Object.fromEntries(
    Object.entries(group).map(([key, value]) => [
      key,
      normaliseFirestoreValue(value, key),
    ]),
  );

  return {
    ioGroupId,
    groupName: String(group.GroupName || group.groupName || "").trim(),
    bandDetails: extractBandDetails(group),
    adviserIds: adviserLinks.map((link) => link.adviserId).filter(Boolean),
    adviserEmails: adviserLinks
      .map((link) => link.adviserEmail)
      .filter(Boolean),
    adviserLinks: normaliseFirestoreValue(adviserLinks, "adviserLinks"),
    bankDetails: existing.bankDetails || {},
    primaryColor,
    secondaryColor: existing.secondaryColor || brandingAdviser.secondaryColor || "",
    tertiaryColor: existing.tertiaryColor || brandingAdviser.tertiaryColor || "",
    logoUrl: existing.logoUrl || "",
    logoStoragePath: existing.logoStoragePath || "",
    group: normalisedGroup,
    source: "io",
    syncedAt,
    updatedAt: syncedAt,
  };
};

export const saveGroupRecord = async (group = {}) => {
  const ioGroupId = String(group.GroupId || group.groupId || "").trim();
  if (!ioGroupId) return null;

  const adviserLinks = await loadGroupAdviserLinks(ioGroupId);
  const currentSnapshot = await getDoc(getGroupDetailsRef(ioGroupId));
  const existing = currentSnapshot.exists() ? currentSnapshot.data() : {};
  const record = buildGroupRecord(group, adviserLinks, existing);

  await setDoc(getGroupDetailsRef(ioGroupId), record, { merge: true });
  return record;
};

export const saveGroupRecords = async (groups = []) => {
  const savedRecords = await Promise.all(
    groups.map((group) => saveGroupRecord(group)),
  );

  return savedRecords.filter(Boolean);
};

export const resolveGroupRecordForIOGroupId = getSavedGroupDetails;

export const saveGroupBankDetailsByIOGroupId = async (
  ioGroupId,
  bankDetails = {},
) => {
  const id = String(ioGroupId || "").trim();
  if (!id) return null;

  const updatedAt = new Date().toISOString();
  const groupRef = getGroupDetailsRef(id);
  const currentSnapshot = await getDoc(groupRef);
  const previousBankDetails = currentSnapshot.exists()
    ? currentSnapshot.data()?.bankDetails || {}
    : {};
  const statementValue = (fieldPath) =>
    Object.prototype.hasOwnProperty.call(bankDetails, fieldPath)
      ? normaliseStatementEmails(bankDetails[fieldPath])
      : normaliseStatementEmails(previousBankDetails[fieldPath]);
  const statementPassword = Object.prototype.hasOwnProperty.call(
    bankDetails,
    "statementPassword",
  )
    ? String(bankDetails.statementPassword || "").replace(/\s+/g, "")
    : String(previousBankDetails.statementPassword || "").replace(/\s+/g, "");
  const payload = {
    ioGroupId: id,
    bankDetails: {
      arSortCode: String(bankDetails.arSortCode || "").trim(),
      arAccountNumber: String(bankDetails.arAccountNumber || "").trim(),
      arAccountHolder: String(bankDetails.arAccountHolder || "").trim(),
      emailCommissionStatementsTo: statementValue(
        "emailCommissionStatementsTo",
      ),
      emailCommissionStatementsCc: statementValue(
        "emailCommissionStatementsCc",
      ),
      statementPassword,
      updatedAt,
    },
    updatedAt,
  };
  const changes = Object.entries(payload.bankDetails)
    .filter(([fieldPath]) => fieldPath !== "updatedAt")
    .map(([fieldPath, newValue]) => {
      const previousValue = previousBankDetails[fieldPath] || "";
      if (String(previousValue) === String(newValue)) return null;

      if (fieldPath === "statementPassword") {
        return {
          fieldPath,
          previousValue: previousValue ? "********" : "",
          newValue: newValue ? "********" : "",
        };
      }

      return {
        fieldPath,
        previousValue,
        newValue,
      };
    })
    .filter(Boolean);
  const updatedBy = auth.currentUser?.email || "";
  const updatedByName =
    auth.currentUser?.displayName || updatedBy || "Unknown user";

  await setDoc(groupRef, {
    ...payload,
    updatedBy,
    updatedByName,
  }, { merge: true });

  if (changes.length > 0) {
    await addDoc(collection(groupRef, "Edit Logs"), {
      ioGroupId: id,
      bankDetails: payload.bankDetails,
      changes,
      changeCount: changes.length,
      updatedAt,
      updatedBy,
      updatedByName,
      editedAt: updatedAt,
      editedBy: updatedBy,
    });
  }

  return payload;
};

export const saveGroupBankDetailsByIOGroupIds = async (
  ioGroupIds = [],
  bankDetails = {},
) => {
  const uniqueGroupIds = [...new Set(toIdList(ioGroupIds))];
  const savedGroups = await Promise.all(
    uniqueGroupIds.map((groupId) =>
      saveGroupBankDetailsByIOGroupId(groupId, bankDetails),
    ),
  );

  return savedGroups.filter(Boolean);
};

export const saveGroupBranding = async (
  ioGroupId,
  { logoUrl = "", logoStoragePath = "", ...colours } = {},
) => {
  const id = String(ioGroupId || "").trim();
  if (!id) return null;

  const updatedAt = new Date().toISOString();
  const payload = {
    ioGroupId: id,
    updatedAt,
    brandingUpdatedAt: updatedAt,
  };

  if (logoUrl) payload.logoUrl = logoUrl;
  if (logoStoragePath) payload.logoStoragePath = logoStoragePath;
  for (const [key, label] of [
    ["primaryColor", "Primary colour"],
    ["secondaryColor", "Secondary colour"],
    ["tertiaryColor", "Tertiary colour"],
  ]) {
    if (!Object.prototype.hasOwnProperty.call(colours, key)) continue;
    const value = String(colours[key] || "").trim().replace(/^#/, "");
    if (value && !/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) {
      throw new Error(`${label} must be a valid 3- or 6-digit hex code, or left empty.`);
    }
    payload[key] = value;
  }

  const adviserColours = {};
  for (const [groupKey, adviserKey] of [
    ["primaryColor", "PrimaryColor"],
    ["secondaryColor", "SecondaryColor"],
    ["tertiaryColor", "TertiaryColor"],
  ]) {
    if (!Object.prototype.hasOwnProperty.call(payload, groupKey)) continue;
    adviserColours[adviserKey] = payload[groupKey];
    adviserColours[groupKey] = payload[groupKey];
  }

  // Resolve current membership from IO Details.groupId, rather than cached links.
  if (Object.keys(adviserColours).length) {
    const adviserLinks = await loadGroupAdviserLinks(id);
    const adviserIds = [...new Set(adviserLinks.map((link) => link.adviserId))];
    // Leave room for the group record in each Firestore batch.
    for (let offset = 0; offset < Math.max(adviserIds.length, 1); offset += 499) {
      const batch = writeBatch(db);
      adviserIds.slice(offset, offset + 499).forEach((adviserId) => {
        batch.update(doc(db, ...ADVISERS_PATH, adviserId), adviserColours);
      });
      batch.set(getGroupDetailsRef(id), payload, { merge: true });
      await batch.commit();
    }
  } else {
    await setDoc(getGroupDetailsRef(id), payload, { merge: true });
  }
  return payload;
};

export const uploadGroupLogo = async (group = {}, file) => {
  const ioGroupId = String(group.GroupId || group.groupId || "").trim();
  const groupName = String(group.GroupName || group.groupName || "").trim();

  if (!ioGroupId) throw new Error("Group ID not found.");
  if (!groupName) throw new Error("Group name not found.");
  if (!file) throw new Error("Select a logo to upload.");
  if (!String(file.type || "").startsWith("image/")) {
    throw new Error("Please upload an image file.");
  }

  const logoStoragePath = `Logos/${groupName}`;
  const logoRef = ref(storage, logoStoragePath);
  const existingGroup = await getSavedGroupDetails(ioGroupId);
  const listedLogos = await listAll(ref(storage, "Logos"));
  const previousLogos = new Map(
    listedLogos.items.filter((item) => {
      if (item.name === groupName) return true;
      const extension = item.name.slice(groupName.length);
      return item.name.startsWith(groupName) &&
        /^\.(png|jpe?g|svg|webp|gif|avif|bmp|ico|tiff?|heic|heif)$/i.test(extension);
    }).map((item) => [item.fullPath, item]),
  );
  if (existingGroup?.logoStoragePath) {
    const previousRef = ref(storage, existingGroup.logoStoragePath);
    if (!previousRef.fullPath.startsWith("Logos/Archived/")) {
      previousLogos.set(previousRef.fullPath, previousRef);
    }
  }

  const archiveVersion = `${new Date().toISOString().replace(/[:.]/g, "-")}-${Math.random().toString(36).slice(2, 10)}`;
  const backups = [];
  // Copy every old variant before overwriting or removing any active logo.
  for (const previousRef of previousLogos.values()) {
    let bytes;
    let metadata;
    try {
      [bytes, metadata] = await Promise.all([
        getBytes(previousRef),
        getMetadata(previousRef),
      ]);
    } catch (error) {
      if (error.code === "storage/object-not-found") continue;
      throw error;
    }
    const backup = {
      source: previousRef,
      bytes,
      metadata: { contentType: metadata.contentType || "application/octet-stream" },
    };
    await uploadBytes(
      ref(storage, `Logos/Archived/${ioGroupId}/${archiveVersion}/${previousRef.name}`),
      backup.bytes,
      backup.metadata,
    );
    backups.push(backup);
  }

  await uploadBytes(logoRef, file, {
    contentType: file.type || "application/octet-stream",
    cacheControl: "no-cache",
  });

  let logoUrl;
  let branding;
  try {
    logoUrl = await getDownloadURL(logoRef);
    branding = await saveGroupBranding(ioGroupId, { logoUrl, logoStoragePath });
  } catch (error) {
    const previousActive = backups.find((backup) => backup.source.fullPath === logoStoragePath);
    if (previousActive) {
      await uploadBytes(logoRef, previousActive.bytes, previousActive.metadata);
    } else {
      await deleteObject(logoRef);
    }
    throw error;
  }

  invalidateLogoCache(groupName);
  for (const backup of backups) {
    if (backup.source.fullPath === logoStoragePath) continue;
    try {
      await deleteObject(backup.source);
    } catch (error) {
      if (error.code !== "storage/object-not-found") {
        throw new Error("The new logo is saved and the old logo is archived, but removing an old active file failed. Please try the upload again.");
      }
    }
  }

  return {
    ...branding,
    logoUrl,
    logoStoragePath,
  };
};
